(function () {
  'use strict';

  if (window.GSJSmoothScroll && window.GSJSmoothScroll.version) return;

  var VERSION = '2.0.0';
  var DESKTOP_MIN_WIDTH = 990;
  var CASH_PATH_PATTERN = /\/pages\/cash-for-gold\/?$/;
  var ANCHOR_OFFSET = 120;
  var SCROLL_LERP = 0.12;

  var root = document.documentElement;
  var body = document.body;
  var lenis = null;
  var resizeTimer = null;
  var bodyObserver = null;
  var mqListenersBound = false;
  var scrollLocked = false;
  var listenersBound = false;

  var PREVENT_SELECTOR = [
    '[data-lenis-prevent]',
    '[data-lenis-prevent-wheel]',
    '[data-lenis-prevent-touch]',
    '[data-lenis-prevent-vertical]',
    '[data-lenis-prevent-horizontal]',
    'textarea',
    'select',
    '[contenteditable="true"]',
    'cart-drawer',
    '#CartDrawer',
    '.cart-drawer',
    '.drawer__inner',
    '.drawer__contents',
    '.drawer__cart-items-wrapper',
    '.cart-drawer__warnings',
    '.cart-drawer__collection',
    'header-drawer',
    'menu-drawer',
    '#menu-drawer',
    '.menu-drawer',
    '.menu-drawer__inner-container',
    '.menu-drawer__navigation-container',
    '.menu-drawer__submenu',
    'details-modal',
    '.header__search',
    '.search-modal',
    '.search-modal__content',
    'predictive-search',
    '.predictive-search',
    'modal-dialog',
    'product-modal',
    '.modal__content',
    '.modal-video',
    '[role="dialog"]',
    'pickup-availability-drawer',
    '.mobile-facets',
    '.mobile-facets__inner',
    '.mobile-facets__main',
    '.facets__display',
    '.facets__display-vertical',
    '.ecom-modal-content',
    '.ecom-modal-content--mobile',
    '.ecom-scroll_bar',
    '.ecom-filter-modal',
    '#ecom-modal-block',
    '.ecom-modal-block--mobile'
  ].join(',');

  var media = {
    desktop: window.matchMedia('(min-width: ' + DESKTOP_MIN_WIDTH + 'px)'),
    finePointer: window.matchMedia('(any-pointer: fine) and (hover: hover)'),
    reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)'),
    print: window.matchMedia('print')
  };

  function isDesignMode() {
    return Boolean(window.Shopify && window.Shopify.designMode) ||
      root.classList.contains('shopify-design-mode');
  }

  function isCashPage() {
    return CASH_PATH_PATTERN.test(window.location.pathname);
  }

  function shouldEnable() {
    return Boolean(
      isCashPage() &&
      typeof window.Lenis === 'function' &&
      media.desktop.matches &&
      media.finePointer.matches &&
      !media.reducedMotion.matches &&
      !media.print.matches &&
      !isDesignMode()
    );
  }

  function isElement(node) {
    return node && node.nodeType === 1;
  }

  function shouldPrevent(node) {
    if (!isElement(node)) return false;

    try {
      return Boolean(node.closest(PREVENT_SELECTOR));
    } catch (error) {
      return false;
    }
  }

  function getAnchorOffset() {
    var header = document.querySelector('.shopify-section-header-sticky, .section-header, sticky-header');
    var offset = ANCHOR_OFFSET;

    if (header) {
      var rect = header.getBoundingClientRect();
      if (rect.height > 0) offset = Math.ceil(rect.height + 24);
    }

    root.style.setProperty('--gsj-smooth-scroll-anchor-offset', offset + 'px');
    return offset;
  }

  function hasScrollLock() {
    if (!body) return false;

    var bodyClasses = Array.prototype.slice.call(body.classList || []);
    var hasOverflowClass = bodyClasses.some(function (className) {
      return className === 'overflow-hidden' ||
        className === 'overflow-hidden-desktop' ||
        className.indexOf('overflow-hidden-') === 0;
    });

    return Boolean(
      hasOverflowClass ||
      body.classList.contains('klaviyo-prevent-body-scrolling') ||
      body.classList.contains('ecom-filter-opened') ||
      body.style.overflow === 'hidden' ||
      root.style.overflow === 'hidden' ||
      window.getComputedStyle(body).overflow === 'hidden' ||
      window.getComputedStyle(root).overflow === 'hidden'
    );
  }

  function syncScrollLock() {
    if (!lenis) return;

    var locked = hasScrollLock();
    if (locked === scrollLocked) return;

    scrollLocked = locked;
    if (locked) {
      lenis.stop();
    } else {
      lenis.start();
    }
  }

  function bindScrollLockObserver() {
    if (bodyObserver || !window.MutationObserver || !body) return;

    bodyObserver = new MutationObserver(syncScrollLock);
    bodyObserver.observe(body, { attributes: true, attributeFilter: ['class', 'style'] });
    bodyObserver.observe(root, { attributes: true, attributeFilter: ['class', 'style'] });
  }

  function unbindScrollLockObserver() {
    if (!bodyObserver) return;
    bodyObserver.disconnect();
    bodyObserver = null;
  }

  function resolveTarget(target) {
    if (!target) return null;
    if (target === window || target === document || target === root || target === body) return root;
    if (typeof target !== 'string') return target;
    if (target === '#top') return root;

    try {
      return document.querySelector(target);
    } catch (error) {
      return null;
    }
  }

  function nativeScrollTo(target, options) {
    var resolvedTarget = resolveTarget(target);
    if (!resolvedTarget) return;

    var scrollOptions = options || {};
    if (resolvedTarget === root) {
      window.scrollTo({ top: 0, behavior: media.reducedMotion.matches ? 'auto' : 'smooth' });
      return;
    }

    var top = resolvedTarget.getBoundingClientRect().top + window.pageYOffset + (scrollOptions.offset || -getAnchorOffset());
    window.scrollTo({ top: Math.max(0, top), behavior: media.reducedMotion.matches ? 'auto' : 'smooth' });
  }

  function scrollTo(target, options) {
    var scrollOptions = options ? Object.assign({}, options) : {};

    if (!Object.prototype.hasOwnProperty.call(scrollOptions, 'offset')) {
      scrollOptions.offset = -getAnchorOffset();
    }

    if (!lenis) {
      nativeScrollTo(target, scrollOptions);
      return;
    }

    lenis.scrollTo(target, scrollOptions);
  }

  function isSamePageHashLink(link) {
    if (!link || !link.hash || link.hash === '#') return false;
    if (link.target || link.hasAttribute('download')) return false;

    return link.origin === window.location.origin &&
      link.pathname.replace(/\/$/, '') === window.location.pathname.replace(/\/$/, '') &&
      link.search === window.location.search;
  }

  function getHashTarget(hash) {
    if (!hash || hash === '#') return null;
    if (hash === '#top') return root;

    try {
      return document.getElementById(decodeURIComponent(hash.slice(1)));
    } catch (error) {
      return null;
    }
  }

  function focusTarget(target) {
    if (!isElement(target) || target === root) return;

    var hadTabIndex = target.hasAttribute('tabindex');
    if (!hadTabIndex) target.setAttribute('tabindex', '-1');

    try {
      target.focus({ preventScroll: true });
    } catch (error) {
      target.focus();
    }

    if (!hadTabIndex) {
      target.addEventListener('blur', function restoreTabIndex() {
        target.removeEventListener('blur', restoreTabIndex);
        target.removeAttribute('tabindex');
      }, { once: true });
    }
  }

  function handleAnchorClick(event) {
    if (!lenis || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    var link = event.target.closest ? event.target.closest('a[href*="#"]') : null;
    if (!isSamePageHashLink(link)) return;

    var target = getHashTarget(link.hash);
    if (!target) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    try {
      window.history.pushState(null, '', link.hash);
    } catch (error) {
      window.location.hash = link.hash;
    }

    lenis.scrollTo(window.pageYOffset || root.scrollTop, { immediate: true, force: true });
    scrollTo(target, {
      lerp: SCROLL_LERP,
      force: true,
      lock: false,
      onComplete: function () {
        focusTarget(target);
      }
    });
  }

  function scrollToCurrentHash() {
    if (!lenis || !window.location.hash || window.location.hash === '#') return;

    var target = getHashTarget(window.location.hash);
    if (!target) return;

    window.requestAnimationFrame(function () {
      scrollTo(target, {
        lerp: SCROLL_LERP,
        force: true,
        onComplete: function () {
          focusTarget(target);
        }
      });
    });
  }

  function resize() {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(function () {
      getAnchorOffset();
      if (lenis) lenis.resize();
    }, 120);
  }

  function bindGlobalListeners() {
    if (listenersBound) return;
    listenersBound = true;

    document.addEventListener('click', handleAnchorClick, true);
    window.addEventListener('popstate', scrollToCurrentHash);
    window.addEventListener('pageshow', scrollToCurrentHash);
    window.addEventListener('resize', resize, { passive: true });
    window.addEventListener('orientationchange', resize, { passive: true });
    window.addEventListener('beforeprint', destroy);
    window.addEventListener('afterprint', refresh);
  }

  function unbindGlobalListeners() {
    if (!listenersBound) return;
    listenersBound = false;

    document.removeEventListener('click', handleAnchorClick, true);
    window.removeEventListener('popstate', scrollToCurrentHash);
    window.removeEventListener('pageshow', scrollToCurrentHash);
    window.removeEventListener('resize', resize);
    window.removeEventListener('orientationchange', resize);
    window.removeEventListener('beforeprint', destroy);
    window.removeEventListener('afterprint', refresh);
  }

  function init() {
    if (lenis || !shouldEnable()) return null;

    lenis = new window.Lenis({
      autoRaf: true,
      lerp: SCROLL_LERP,
      smoothWheel: true,
      wheelMultiplier: 1,
      syncTouch: false,
      touchMultiplier: 1,
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      overscroll: true,
      autoResize: true,
      infinite: false,
      anchors: true,
      stopInertiaOnNavigate: true,
      prevent: shouldPrevent
    });

    api.instance = lenis;
    root.classList.add('gsj-smooth-scroll-ready');
    getAnchorOffset();
    bindGlobalListeners();
    bindScrollLockObserver();
    scrollLocked = !hasScrollLock();
    syncScrollLock();
    scrollToCurrentHash();

    return lenis;
  }

  function destroy() {
    if (!lenis) return;

    unbindGlobalListeners();
    unbindScrollLockObserver();

    lenis.destroy();
    lenis = null;
    api.instance = null;
    scrollLocked = false;
    root.classList.remove('gsj-smooth-scroll-ready');
    root.style.removeProperty('--gsj-smooth-scroll-anchor-offset');
  }

  function refresh() {
    if (shouldEnable()) {
      init();
    } else {
      destroy();
    }
  }

  function addMediaListener(query) {
    if (query.addEventListener) {
      query.addEventListener('change', refresh);
    } else if (query.addListener) {
      query.addListener(refresh);
    }
  }

  function bindMediaListeners() {
    if (mqListenersBound) return;
    mqListenersBound = true;

    Object.keys(media).forEach(function (key) {
      addMediaListener(media[key]);
    });
  }

  function start() {
    if (lenis) lenis.start();
  }

  function stop() {
    if (lenis) lenis.stop();
  }

  var api = {
    version: VERSION,
    instance: null,
    init: init,
    destroy: destroy,
    start: start,
    stop: stop,
    resize: resize,
    scrollTo: scrollTo
  };

  window.GSJSmoothScroll = api;

  function boot() {
    body = document.body;
    bindMediaListeners();
    refresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  window.GSJSmoothScrollSettings = Object.freeze({
    activeLerp: SCROLL_LERP,
    activeWheelMultiplier: 1
  });
})();
